import React, {
  createContext,
  useContext,
  useReducer,
  ReactNode,
  useCallback,
} from "react";

// Define the shape of theme state
interface ThemeState {
  organizationColors: {
    primary?: string;
    secondary?: string;
    accent?: string;
    background?: string;
    text?: string;
  };
  organizationId?: string;
  organizationName?: string;
  logoId?: string;
  iconLogoId?: string;
  loading: boolean;
}

// Define the actions that can be dispatched
type ThemeAction =
  | {
      type: "SET_ORGANIZATION_THEME";
      payload: {
        organizationId: string;
        organizationName: string;
        colors: ThemeState["organizationColors"];
        logoId?: string;
        iconLogoId?: string;
      };
    }
  | { type: "UPDATE_COLORS"; payload: ThemeState["organizationColors"] }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "RESET_THEME" };

// Define the context type
interface ThemeContextType {
  state: ThemeState;
  dispatch: React.Dispatch<ThemeAction>;
  // Helper functions
  setOrganizationTheme: (data: {
    organizationId: string;
    organizationName: string;
    colors: ThemeState["organizationColors"];
    logoId?: string;
    iconLogoId?: string;
  }) => void;
  updateColors: (colors: ThemeState["organizationColors"]) => void;
  setLoading: (loading: boolean) => void;
  resetTheme: () => void;
  // Computed values
  getPrimaryColor: () => string;
  getSecondaryColor: () => string;
  getAccentColor: () => string;
}

// Default colors fallback
const DEFAULT_COLORS = {
  primary: "#ef4444", // red-500
  secondary: "#6b7280", // gray-500
  accent: "#3b82f6", // blue-500
  background: "#ffffff",
  text: "#111827", // gray-900
};

// Create the context
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Reducer function
function themeReducer(state: ThemeState, action: ThemeAction): ThemeState {
  switch (action.type) {
    case "SET_ORGANIZATION_THEME":
      // Prevent unnecessary updates if the organization is already set
      if (state.organizationId === action.payload.organizationId) {
        return state;
      }
      return {
        ...state,
        organizationId: action.payload.organizationId,
        organizationName: action.payload.organizationName,
        organizationColors: action.payload.colors,
        logoId: action.payload.logoId,
        iconLogoId: action.payload.iconLogoId,
        loading: false,
      };
    case "UPDATE_COLORS":
      return {
        ...state,
        organizationColors: {
          ...state.organizationColors,
          ...action.payload,
        },
      };
    case "SET_LOADING":
      return { ...state, loading: action.payload };
    case "RESET_THEME":
      return {
        organizationColors: {},
        loading: false,
      };
    default:
      return state;
  }
}

// Provider component
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(themeReducer, {
    organizationColors: {},
    loading: false,
  });

  // Helper functions (memoized to prevent infinite re-renders)
  const setOrganizationTheme = useCallback(
    (data: {
      organizationId: string;
      organizationName: string;
      colors: ThemeState["organizationColors"];
      logoId?: string;
      iconLogoId?: string;
    }) => {
      dispatch({ type: "SET_ORGANIZATION_THEME", payload: data });
    },
    [],
  );

  const updateColors = useCallback(
    (colors: ThemeState["organizationColors"]) => {
      dispatch({ type: "UPDATE_COLORS", payload: colors });
    },
    [],
  );

  const setLoading = useCallback((loading: boolean) => {
    dispatch({ type: "SET_LOADING", payload: loading });
  }, []);

  const resetTheme = useCallback(() => {
    dispatch({ type: "RESET_THEME" });
  }, []);

  // Computed values with fallbacks (memoized)
  const getPrimaryColor = useCallback(() => {
    return state.organizationColors.primary || DEFAULT_COLORS.primary;
  }, [state.organizationColors.primary]);

  const getSecondaryColor = useCallback(() => {
    return state.organizationColors.secondary || DEFAULT_COLORS.secondary;
  }, [state.organizationColors.secondary]);

  const getAccentColor = useCallback(() => {
    return state.organizationColors.accent || DEFAULT_COLORS.accent;
  }, [state.organizationColors.accent]);

  return (
    <ThemeContext.Provider
      value={{
        state,
        dispatch,
        setOrganizationTheme,
        updateColors,
        setLoading,
        resetTheme,
        getPrimaryColor,
        getSecondaryColor,
        getAccentColor,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

// Custom hook to use the theme context
export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}

// Helper hook for just getting colors (most common use case)
export function useThemeColors() {
  const { state, getPrimaryColor, getSecondaryColor, getAccentColor } =
    useTheme();

  return {
    colors: state.organizationColors,
    primary: getPrimaryColor(),
    secondary: getSecondaryColor(),
    accent: getAccentColor(),
    background:
      state.organizationColors.background || DEFAULT_COLORS.background,
    text: state.organizationColors.text || DEFAULT_COLORS.text,
  };
}

// Utility hook for applying theme styles
export function useThemeStyles() {
  const { primary, secondary, accent, background, text } = useThemeColors();

  return {
    primaryButton: {
      backgroundColor: primary,
      borderColor: primary,
    },
    secondaryButton: {
      backgroundColor: "transparent",
      borderColor: secondary,
      color: secondary,
    },
    accentText: {
      color: accent,
    },
    primaryText: {
      color: primary,
    },
    primaryBorder: {
      borderColor: primary,
    },
    secondaryBorder: {
      borderColor: secondary,
    },
  };
}
